import { useEffect, useState } from 'react';
import type { AdminAccountActivity, AdminActivityItem, AdminActivityKind } from '@boardflight/shared';
import { api } from '../api.js';
import { Async, Filter, Panel, Time, duration, useAsync } from '../ui.js';

const GROUP_FILTER = [
  { value: '', label: 'Everything' },
  { value: 'board', label: 'Board' },
  { value: 'store', label: 'Library' },
  { value: 'dashboard', label: 'Dashboard' },
] as const;

const KIND_LABELS: Record<AdminActivityKind, string> = {
  // The desktop app's sessions are this kind too, and the detail names which client it was.
  board_sign_in: 'Signed in',
  install: 'Install',
  presence: 'Seen on Board',
  feedback: 'Sent feedback',
  // The press, and the Board's yes to it. Whether the game then ran is the game's to report.
  launch: 'Pressed Play',
  client_event: 'Board app',
  store_view: 'Viewed in library',
  library_add: 'Added to My Games',
  code_redeemed: 'Redeemed a code',
  access_granted: 'Given access',
  signup: 'Signed up',
  sign_in: 'Signed in',
  team_joined: 'Joined a team',
  playground: 'Playground sandbox',
  team_action: 'Team action',
};

/** `…7Q2KXM` — enough of a device id to tell one person's Boards apart, which is all it is for. */
const boardLabel = (id: string) => `Board …${id.slice(-6)}`;

/**
 * The account's Boards, and the games presence last saw on them (§3.4) — the Boards tab.
 *
 * The same read as the Activity tab, narrowed to the Board group: the Boards and games come back
 * with every page whatever the group, and this way a tab opened for its two tables does not also
 * walk the store and the audit log.
 */
export function AccountBoards({ userId }: { userId: string }) {
  const read = useAsync(
    () => api<AdminAccountActivity>(`/v1/admin/accounts/${userId}/activity?group=board`),
    [userId],
  );

  return (
    <Async state={read}>
      {(data) => (
        <>
          <Panel title="Boards">
            {data.boards.length === 0 ? (
              <p className="muted">Never signed in on a Board.</p>
            ) : (
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Board</th>
                      <th>First seen</th>
                      <th>Last seen</th>
                      <th className="num">Games on it</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.boards.map((board) => (
                      <tr key={board.id}>
                        <td className="mono" title={board.id}>{boardLabel(board.id)}</td>
                        <td className="muted"><Time at={board.firstSeenAt} /></td>
                        <td className="muted"><Time at={board.lastSeenAt} /></td>
                        <td className="num">{board.installed}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Panel>

          <Panel title="Games on these Boards">
            {data.games.length === 0 ? (
              <p className="muted">Presence has seen no game on them.</p>
            ) : (
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Game</th>
                      <th>Studio</th>
                      <th>Version</th>
                      <th>Board</th>
                      <th>First seen</th>
                      <th>Last seen</th>
                      <th>Uninstalled</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.games.map((game) => (
                      <tr key={`${game.appId}:${game.deviceId}`}>
                        <td>
                          <a href={`#games/${game.appId}`}>{game.appName ?? game.appId}</a>
                        </td>
                        <td className="muted">{game.teamName ?? '—'}</td>
                        <td className="mono">{game.versionName ?? '—'}</td>
                        <td className="mono muted" title={game.deviceId}>{boardLabel(game.deviceId)}</td>
                        <td className="muted"><Time at={game.firstSeenAt} /></td>
                        <td className="muted"><Time at={game.lastSeenAt} /></td>
                        <td className="muted"><Time at={game.uninstalledAt} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Panel>
        </>
      )}
    </Async>
  );
}

/**
 * Everything the account did, newest first (§3.4) — the Activity tab.
 *
 * Built from rows that name the account and nothing else, and never a play: plays are not tied to
 * accounts. The panel says so, and what the empty stretches might be, because three of the sources
 * are pruned and a quiet month can be a retention window rather than a quiet month.
 */
export function AccountActivity({ userId }: { userId: string }) {
  const [group, setGroup] = useState('');
  const first = useAsync(
    () =>
      api<AdminAccountActivity>(
        `/v1/admin/accounts/${userId}/activity${group ? `?group=${group}` : ''}`,
      ),
    [userId, group],
  );

  // Older pages, appended. Reset whenever the first page is read again — another account, another
  // group, or a reload — so a cursor from one list is never followed in another.
  const [older, setOlder] = useState<AdminActivityItem[]>([]);
  const [next, setNext] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    setOlder([]);
    setNext(first.data?.next ?? null);
    setError(null);
  }, [first.data]);

  async function loadOlder() {
    if (!next) return;
    setBusy(true);
    setError(null);
    try {
      const page = await api<AdminAccountActivity>(
        `/v1/admin/accounts/${userId}/activity?before=${encodeURIComponent(next)}${group ? `&group=${group}` : ''}`,
      );
      setOlder((rows) => [...rows, ...page.items]);
      setNext(page.next);
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : String(failure));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Panel
      title="Activity"
      actions={<Filter label="From" value={group} onChange={setGroup} options={GROUP_FILTER} />}
    >
      <Async state={first}>
        {(data) => {
          const items = [...data.items, ...older];
          return (
            <>
              <p
                className="note"
                title={`Library views go back ${data.retentionDays.storeEvents} days, presence ${data.retentionDays.analytics}, Board app events ${data.retentionDays.clientEvents}; installs are pruned on the API's own schedule. A sign-in is listed once; its last used is its newest refresh, which every running client makes, and that is what moves Last active.`}
              >
                No plays: plays are not tied to accounts. Pressed Play is Play in the desktop app that the Board accepted.
              </p>
              {items.length === 0 ? (
                <p className="muted">Nothing recorded.</p>
              ) : (
                <div className="table-wrap">
                  <table>
                    <thead>
                      <tr>
                        <th>When</th>
                        <th>What</th>
                        <th>Game</th>
                        <th>Detail</th>
                        <th>Board</th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((item) => (
                        <tr key={item.key}>
                          <td className="muted"><Time at={item.at} /></td>
                          <td>{KIND_LABELS[item.kind] ?? item.kind}</td>
                          <td>
                            {item.appId ? (
                              <>
                                <a href={`#games/${item.appId}`}>{item.appName ?? item.appId}</a>
                                {item.teamName ? <span className="muted"> · {item.teamName}</span> : null}
                              </>
                            ) : item.teamName ? (
                              <span className="muted">{item.teamName}</span>
                            ) : (
                              <span className="muted">—</span>
                            )}
                          </td>
                          <td className="mono small">
                            {item.detail ?? ''}
                            {item.durationMs !== null ? ` · ${duration(item.durationMs)}` : ''}
                            {item.lastUsedAt !== null ? (
                              <> · last used <Time at={item.lastUsedAt} /></>
                            ) : null}
                          </td>
                          <td className="mono muted" title={item.deviceId ?? undefined}>
                            {item.deviceId ? boardLabel(item.deviceId) : ''}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {next ? (
                <div className="row">
                  <button disabled={busy} onClick={() => void loadOlder()}>
                    {busy ? 'Loading…' : 'Load older'}
                  </button>
                </div>
              ) : null}
              {error ? <p className="error">{error}</p> : null}
            </>
          );
        }}
      </Async>
    </Panel>
  );
}
